import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';
import { FlatAmountInput } from '~/modules/matters/modals/shared/FlatAmountInput';
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldPath,
} from 'react-hook-form';
import { HOURLY_FEE_OPTIONS } from '~/modules/connect/feeSchedule';
import type { FeeScheduleData } from '~/modules/connect/feeSchedule';
import { formatCurrency } from '~/shared/utils/formatCurrency';
import {
  BILLING_TYPE_FLAT,
  BILLING_TYPE_HOURLY,
  RETAINER_FEE_BREAKDOWN_OPTIONS,
  type BillingType,
  type RateKey,
  RATE_KEY_OTHER,
} from '~/modules/matters/modals/requestRetainerSchema';
import type { RetainerFeeBreakdownType } from '~/gql/graphql';
import {
  BillingLabel,
  FeeTypeFormControl,
} from '~/modules/matters/modals/shared/retainerModalStyles';

export type FeeTypeOption = RetainerFeeBreakdownType;

export interface BillingFormSlice {
  feeType?: FeeTypeOption | '';
  billingType: BillingType;
  flatAmount: number;
  rateKey: RateKey;
  customRate?: number;
  customServiceLabel?: string;
  hours: number;
}

interface Props<TForm extends BillingFormSlice> {
  control: Control<TForm>;
  errors: FieldErrors<TForm>;
  feeSchedule: FeeScheduleData;
  formIdPrefix: string;
  isProBono: boolean;
  isFlatRate: boolean;
  rateKey: RateKey;
  hoursLabel?: string;
  flatAmountPlaceholder?: string;
}

function RetainerBillingFieldsInner<TForm extends BillingFormSlice>({
  control,
  errors,
  feeSchedule,
  formIdPrefix,
  isProBono,
  isFlatRate,
  rateKey,
  hoursLabel = 'Hours requested',
  flatAmountPlaceholder,
}: Props<TForm>) {
  return (
    <>
      <BillingLabel variant="subtitle2" color="text.secondary">
        Billing
      </BillingLabel>
      <Controller<TForm>
        name={'feeType' as FieldPath<TForm>}
        control={control}
        render={({ field }) => (
          <FeeTypeFormControl fullWidth>
            <InputLabel id={`${formIdPrefix}-feetype-label`} shrink>
              Fee breakdown
            </InputLabel>
            <Select
              {...field}
              labelId={`${formIdPrefix}-feetype-label`}
              label="Fee breakdown"
              displayEmpty
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              {RETAINER_FEE_BREAKDOWN_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FeeTypeFormControl>
        )}
      />
      {!isProBono && (
        <>
          <Controller<TForm>
            name={'billingType' as FieldPath<TForm>}
            control={control}
            render={({ field }) => (
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  {...field}
                  onChange={(e) =>
                    field.onChange(e.target.value as TForm['billingType'])
                  }
                  aria-label="Billing"
                >
                  <FormControlLabel
                    value={BILLING_TYPE_HOURLY}
                    control={<Radio size="small" />}
                    label="Hourly (hours × rate)"
                  />
                  <FormControlLabel
                    value={BILLING_TYPE_FLAT}
                    control={<Radio size="small" />}
                    label="Flat rate (single fee)"
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
          {isFlatRate ? (
            <Controller<TForm>
              name={'flatAmount' as FieldPath<TForm>}
              control={control}
              render={({ field }) => (
                <FlatAmountInput
                  value={(field.value as number | undefined) ?? 0}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  placeholder={flatAmountPlaceholder}
                  error={Boolean(errors.flatAmount)}
                  helperText={
                    (errors.flatAmount as { message?: string })?.message
                  }
                />
              )}
            />
          ) : (
            <>
              <Controller<TForm>
                name={'rateKey' as FieldPath<TForm>}
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={Boolean(errors.rateKey)}>
                    <InputLabel id={`${formIdPrefix}-rate-label`}>
                      Hourly fee for this task
                    </InputLabel>
                    <Select
                      {...field}
                      labelId={`${formIdPrefix}-rate-label`}
                      label="Hourly fee for this task"
                      onChange={(e) =>
                        field.onChange(e.target.value as TForm['rateKey'])
                      }
                    >
                      {HOURLY_FEE_OPTIONS.map(({ key, label }) => (
                        <MenuItem key={key} value={key}>
                          {label} — {formatCurrency(feeSchedule[key])}/hr
                        </MenuItem>
                      ))}
                      <MenuItem value={RATE_KEY_OTHER}>
                        Other (custom rate) — enter below
                      </MenuItem>
                    </Select>
                    {(errors.rateKey as { message?: string })?.message && (
                      <FormHelperText>
                        {(errors.rateKey as { message?: string }).message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              {rateKey === RATE_KEY_OTHER && (
                <>
                  <Controller<TForm>
                    name={'customRate' as FieldPath<TForm>}
                    control={control}
                    rules={{
                      required: 'Custom rate is required',
                      min: {
                        value: 100,
                        message: 'Custom rate must be at least $100/hr',
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Custom hourly rate"
                        required
                        inputProps={{ min: 100, step: 50 }}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value) || 0)
                        }
                        error={Boolean(errors.customRate)}
                        helperText={
                          (errors.customRate as { message?: string })
                            ?.message ??
                          'Min $100/hr for uncommon services (e.g. slide review)'
                        }
                        fullWidth
                      />
                    )}
                  />
                  <Controller<TForm>
                    name={'customServiceLabel' as FieldPath<TForm>}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Service type (optional)"
                        placeholder="e.g. Slide review"
                        fullWidth
                      />
                    )}
                  />
                </>
              )}
              <Controller<TForm>
                name={'hours' as FieldPath<TForm>}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={hoursLabel}
                    required
                    inputProps={{ min: 0.5, step: 0.5 }}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    error={Boolean(errors.hours)}
                    helperText={(errors.hours as { message?: string })?.message}
                    fullWidth
                  />
                )}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

export default RetainerBillingFieldsInner;
